<template>
  <HrPageLayout
    title="Compliance Repositories"
    subtitle="Circulars, Scheme of Service and Salary Review documents — searchable with CSV & PDF reports"
    icon="📁"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Compliance Module' },
      { label: 'Repositories' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="load">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !rows.length" @click="exportCsv">
        <i class="bx bx-file"></i> CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !rows.length" @click="exportPdf">
        <i class="bx bxs-file-pdf"></i> PDF
      </button>
      <button v-if="canManage" type="button" class="hr-btn hr-btn-primary" @click="openNew">
        <i class="bx bx-plus"></i> Add
      </button>
    </template>

    <div class="cr">
      <div class="cr-tabs">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="cr-tab"
          :class="{ active: repo === t.id }"
          @click="switchRepo(t.id)"
        >{{ t.label }}</button>
      </div>

      <div class="cr-filters">
        <label>Search
          <input v-model="filters.search" type="text" placeholder="Name, reference…" @keyup.enter="load">
        </label>
        <label>From
          <input v-model="filters.date_from" type="date">
        </label>
        <label>To
          <input v-model="filters.date_to" type="date">
        </label>
        <label v-if="repo === 'salary'">Status
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>
        </label>
        <button type="button" class="hr-btn hr-btn-primary" @click="load">Apply filters</button>
      </div>

      <div class="cr-meta">
        <strong>{{ total }}</strong> document{{ total === 1 ? '' : 's' }}
        <span class="muted">· {{ currentLabel }}</span>
      </div>

      <div v-if="loading" class="muted pad">Loading…</div>
      <div v-else class="cr-card">
        <table class="cr-table">
          <thead>
            <tr>
              <th>#</th>
              <th v-for="h in headers" :key="h.key">{{ h.label }}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in rows" :key="r.id">
              <td>{{ i + 1 }}</td>
              <td v-for="h in headers" :key="h.key">{{ cell(r, h.key) }}</td>
              <td class="acts">
                <button v-if="r.file_path" type="button" class="hr-btn hr-btn-sm" @click="download(r)">File</button>
                <button v-if="canManage" type="button" class="hr-btn hr-btn-sm" @click="edit(r)">Edit</button>
                <button v-if="canManage" type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="remove(r)">Delete</button>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="headers.length + 2" class="muted pad">No documents yet. Use Add to upload.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="show" class="cr-ov" @click.self="show = false">
      <div class="cr-modal" @click.stop>
        <div class="cr-mh">
          <h3>{{ form.id ? 'Edit' : 'Add' }} — {{ currentLabel }}</h3>
          <button type="button" class="cr-x" @click="show = false">&times;</button>
        </div>
        <div class="cr-mb">
          <template v-if="repo === 'circulars'">
            <label>Document name * <input v-model="form.document_name"></label>
            <label>Reference No. <input v-model="form.reference_no"></label>
            <label>Circular date <input v-model="form.circular_date" type="date"></label>
            <label class="full">Description <textarea v-model="form.description" rows="3"></textarea></label>
          </template>
          <template v-else-if="repo === 'scheme'">
            <label>Document name * <input v-model="form.document_name"></label>
            <label>Cadre / stream <input v-model="form.cadre"></label>
            <label>Department <input v-model="form.department_name"></label>
            <label class="full">Description <textarea v-model="form.description" rows="3"></textarea></label>
          </template>
          <template v-else>
            <label>Review title * <input v-model="form.review_title"></label>
            <label>Cycle name <input v-model="form.cycle_name" placeholder="e.g. FY 2026/2027"></label>
            <label>Effective date <input v-model="form.effective_date" type="date"></label>
            <label>End date <input v-model="form.end_date" type="date"></label>
            <label>Status
              <select v-model="form.status">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label class="full">Description <textarea v-model="form.description" rows="3"></textarea></label>
          </template>
          <label class="full">Document file
            <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xls,.xlsx" @change="onFile">
            <small v-if="form.original_name || form.id">{{ form.original_name || 'Existing file kept if no new file chosen' }}</small>
          </label>
        </div>
        <div class="cr-ma">
          <button type="button" class="hr-btn hr-btn-secondary" @click="show = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast, getRight } from "@/api";
import {
  downloadCsv,
  downloadCountyTablePdf,
  periodLabelFromFilters,
} from "@/utils/countyReportExport";

const HEADERS = {
  circulars: [
    { key: "document_name", label: "Document" },
    { key: "reference_no", label: "Ref." },
    { key: "circular_date", label: "Circular date" },
    { key: "uploaded_by", label: "Uploaded by" },
    { key: "created_at", label: "Uploaded" },
    { key: "updated_by", label: "Last edited by" },
    { key: "updated_at", label: "Updated" },
    { key: "original_name", label: "File" },
  ],
  scheme: [
    { key: "document_name", label: "Name" },
    { key: "cadre", label: "Cadre" },
    { key: "department_name", label: "Department" },
    { key: "uploaded_by", label: "Uploaded by" },
    { key: "created_at", label: "Uploaded" },
    { key: "updated_by", label: "Last edited by" },
    { key: "updated_at", label: "Updated" },
    { key: "original_name", label: "File" },
  ],
  salary: [
    { key: "review_title", label: "Title" },
    { key: "cycle_name", label: "Cycle" },
    { key: "effective_date", label: "Effective" },
    { key: "end_date", label: "End" },
    { key: "status", label: "Status" },
    { key: "created_by", label: "Created by" },
    { key: "created_at", label: "Created" },
    { key: "updated_by", label: "Edited by" },
    { key: "updated_at", label: "Edited" },
    { key: "original_name", label: "File" },
  ],
};

function emptyForm(repo) {
  if (repo === "salary") {
    return {
      id: null,
      review_title: "",
      cycle_name: "",
      effective_date: "",
      end_date: "",
      status: "draft",
      description: "",
      original_name: "",
      file: null,
    };
  }
  if (repo === "scheme") {
    return {
      id: null,
      document_name: "",
      cadre: "",
      department_name: "",
      description: "",
      original_name: "",
      file: null,
    };
  }
  return {
    id: null,
    document_name: "",
    reference_no: "",
    circular_date: "",
    description: "",
    original_name: "",
    file: null,
  };
}

export default {
  name: "ComplianceRepositories",
  components: { HrPageLayout },
  data() {
    return {
      repo: "circulars",
      tabs: [
        { id: "circulars", label: "Circulars Repository" },
        { id: "scheme", label: "Scheme of Service" },
        { id: "salary", label: "Salary Review" },
      ],
      filters: { search: "", date_from: "", date_to: "", status: "" },
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      saving: false,
      show: false,
      form: emptyForm("circulars"),
      canManage: true,
    };
  },
  computed: {
    headers() {
      return HEADERS[this.repo] || HEADERS.circulars;
    },
    currentLabel() {
      return (this.tabs.find((t) => t.id === this.repo) || {}).label || "";
    },
  },
  mounted() {
    this.canManage = getRight([
      "EMPLOYEES COMPLIANCE MANAGE",
      "COMPLIANCE_UNIT_MANAGE",
      "CIRCULARS REPOSITORY",
      "SCHEME OF SERVICE REPOSITORY",
      "SALARY REVIEW REPOSITORY",
      "USER MANAGEMENT",
      "CATEGORY & ROLES",
    ]);
    this.load();
  },
  methods: {
    cell(r, key) {
      const v = r[key];
      if (v == null || v === "") return "—";
      if (String(key).endsWith("_at") || key.includes("date")) {
        return String(v).replace("T", " ").slice(0, 19);
      }
      return String(v);
    },
    switchRepo(id) {
      this.repo = id;
      this.form = emptyForm(id);
      this.load();
    },
    buildFd(extra = {}) {
      const fd = new FormData();
      fd.append("function", "complianceRepoList");
      fd.append("repo_type", this.repo);
      fd.append("search", this.filters.search || "");
      fd.append("date_from", this.filters.date_from || "");
      fd.append("date_to", this.filters.date_to || "");
      fd.append("status", this.filters.status || "");
      fd.append("limit", String(extra.limit || 100));
      fd.append("page", String(extra.page || 1));
      if (extra.exportAll) fd.append("exportAll", "1");
      return fd;
    },
    async load() {
      this.loading = true;
      try {
        const res = await execute(this.buildFd(), authUrl);
        if (res?.data?.success) {
          this.rows = res.data.data?.rows || [];
          this.total = Number(res.data.data?.total || this.rows.length);
          if (res.data.data?.permissions) {
            this.canManage = !!res.data.data.permissions.manage;
          }
        } else {
          this.rows = [];
          toast.error(res?.data?.message || "Load failed");
        }
      } catch (_) {
        this.rows = [];
        toast.error("Load failed");
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.form = emptyForm(this.repo);
      this.show = true;
    },
    edit(r) {
      this.form = {
        ...emptyForm(this.repo),
        ...r,
        circular_date: (r.circular_date || "").toString().slice(0, 10),
        effective_date: (r.effective_date || "").toString().slice(0, 10),
        end_date: (r.end_date || "").toString().slice(0, 10),
        file: null,
      };
      this.show = true;
    },
    onFile(e) {
      this.form.file = e?.target?.files?.[0] || null;
      if (this.form.file) this.form.original_name = this.form.file.name;
    },
    async save() {
      const title = this.repo === "salary" ? this.form.review_title : this.form.document_name;
      if (!String(title || "").trim()) {
        toast.error(this.repo === "salary" ? "Review title required" : "Document name required");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "complianceRepoSave");
        fd.append("repo_type", this.repo);
        if (this.form.id) fd.append("id", String(this.form.id));
        const skip = ["file", "id"];
        Object.keys(this.form).forEach((k) => {
          if (skip.includes(k)) return;
          if (this.form[k] != null && this.form[k] !== "") fd.append(k, String(this.form[k]));
        });
        if (this.form.file) fd.append("file", this.form.file, this.form.file.name);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Saved");
          this.show = false;
          this.load();
        } else toast.error(res?.data?.message || "Save failed");
      } finally {
        this.saving = false;
      }
    },
    async remove(r) {
      if (!window.confirm("Delete this document?")) return;
      const fd = new FormData();
      fd.append("function", "complianceRepoDelete");
      fd.append("repo_type", this.repo);
      fd.append("id", String(r.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Deleted");
        this.load();
      } else toast.error(res?.data?.message || "Delete failed");
    },
    async download(r) {
      const fd = new FormData();
      fd.append("function", "complianceRepoDownload");
      fd.append("repo_type", this.repo);
      fd.append("id", String(r.id));
      try {
        const tokenStr = sessionStorage.getItem("tokenSession");
        const axios = (await import("axios")).default;
        const res = await axios.post(authUrl, fd, {
          headers: { Authorization: `Bearer ${tokenStr}` },
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = r.original_name || `document_${r.id}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (_) {
        toast.error("Download failed");
      }
    },
    async fetchAll() {
      const res = await execute(this.buildFd({ exportAll: true, limit: 5000 }), authUrl);
      if (!res?.data?.success) throw new Error(res?.data?.message || "Export failed");
      return res.data.data?.rows || [];
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const all = await this.fetchAll();
        downloadCsv(
          this.headers.map((h) => h.label),
          all.map((r) => this.headers.map((h) => r[h.key] ?? "")),
          `compliance_${this.repo}_${Date.now()}.csv`
        );
        toast.success(`CSV (${all.length} rows)`);
      } catch (e) {
        toast.error(e?.message || "CSV failed");
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exporting = true;
      try {
        const all = await this.fetchAll();
        await downloadCountyTablePdf({
          title: `${this.currentLabel} Report`.toUpperCase(),
          subtitle: "Compliance Module · Document Repository",
          periodLabel: periodLabelFromFilters(this.filters) || new Date().toLocaleString("en-KE"),
          headers: this.headers.map((h) => h.label),
          rows: all.map((r) => this.headers.map((h) => r[h.key] ?? "")),
          filename: `compliance_${this.repo}_${Date.now()}.pdf`,
          summaryRows: [
            ["Records", String(all.length)],
            ["Repository", this.currentLabel],
            ["Search", this.filters.search || "—"],
          ],
        });
        toast.success("PDF downloaded");
      } catch (e) {
        toast.error(e?.message || "PDF failed");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.cr { display: flex; flex-direction: column; gap: 1rem; }
.cr-tabs { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.cr-tab {
  border: 1px solid #e2e8f0; background: #fff; border-radius: 999px;
  padding: 0.4rem 0.9rem; font-size: 0.85rem; font-weight: 600; color: #475569; cursor: pointer;
}
.cr-tab.active { background: #0c4a6e; color: #fff; border-color: #0c4a6e; }
.cr-filters {
  display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr auto; gap: 0.65rem;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.9rem 1rem; align-items: end;
}
.cr-filters label {
  display: flex; flex-direction: column; gap: 0.3rem;
  font-size: 0.72rem; font-weight: 700; color: #64748b;
}
.cr-filters input, .cr-filters select {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.45rem 0.6rem; background: #f8fafc; color: #0f172a;
}
.cr-meta { font-size: 0.88rem; color: #334155; }
.muted { color: #94a3b8; }
.pad { padding: 1.5rem; }
.cr-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto; }
.cr-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.cr-table th, .cr-table td { padding: 0.6rem 0.7rem; border-bottom: 1px solid #f1f5f9; text-align: left; }
.cr-table th { background: #f8fafc; font-size: 0.7rem; text-transform: uppercase; color: #64748b; }
.acts { display: flex; gap: 4px; flex-wrap: wrap; white-space: nowrap; }
.cr-ov {
  position: fixed; inset: 0; background: rgba(15,23,42,.5);
  display: flex; align-items: center; justify-content: center; z-index: 10050; padding: 1rem;
}
.cr-modal { background: #fff; width: min(620px, 96vw); border-radius: 14px; max-height: 92vh; overflow: auto; }
.cr-mh { display: flex; justify-content: space-between; padding: 1rem 1.15rem; border-bottom: 1px solid #e2e8f0; }
.cr-mh h3 { margin: 0; font-size: 1.05rem; }
.cr-x { border: none; background: none; font-size: 1.4rem; cursor: pointer; }
.cr-mb { padding: 1rem 1.15rem; display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }
.cr-mb label { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.75rem; font-weight: 700; color: #475569; }
.cr-mb label.full { grid-column: 1 / -1; }
.cr-mb input, .cr-mb select, .cr-mb textarea {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem 0.65rem; background: #f8fafc;
}
.cr-mb small { font-weight: 500; color: #64748b; }
.cr-ma { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0 1.15rem 1rem; }
@media (max-width: 800px) {
  .cr-filters { grid-template-columns: 1fr 1fr; }
  .cr-mb { grid-template-columns: 1fr; }
}
</style>
